/** @jsxImportSource @emotion/react */
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { Button } from '../../../design-system/atoms/Button/Button';
import { Spinner } from '../../../design-system/atoms/Spinner/Spinner';
import { useDocViewer } from '../../hooks/useDocViewer';
import {
  metaStyle,
  titleStyle,
  submetaStyle,
  actionsStyle,
  msgStyle,
  fallbackStyle,
  mdStyle,
} from './DocViewer.styles';

export interface DocViewerProps {
  /** 모듈명 (예: comp_earnings). doc-file 조회 경로에 사용. */
  module: string;
  /** 문서 제목 (source_meta.title 또는 title 컬럼). */
  title?: string;
  /** 발행사 등 부가 정보. 없으면 표시 안 함. */
  publisher?: string;
  /** 문서 일자 (doc_date). */
  docDate?: string;
  /** 원본 파일 URL (source_url). md를 못 받을 때의 폴백 경로이자 상시 제공 링크. */
  sourceUrl?: string;
  /** 변환 마크다운 경로 (md_path). 없으면 문서 뷰어를 채울 수 없다. */
  mdPath: string | null;
  /** 변환 이미지 경로 목록 (image_paths). */
  imagePaths: string[];
  /** 뷰어가 열려 있는지 — 닫혀 있으면 파일을 받지 않는다. */
  open: boolean;
}

/**
 * 원시 HTML 허용 시 적용할 sanitize 스키마.
 *
 * docparser 변환 결과는 표를 `<table>` HTML로 내보내는데, react-markdown은 원시 HTML을
 * 기본적으로 버린다 → rehype-raw로 살린다. 다만 이 md의 뿌리는 외부 CDN 문서이고 이 콘솔은
 * 토큰을 들고 있으므로, raw만 켜면 문서 안의 스크립트가 그대로 실행된다. 그래서 항상 sanitize를
 * 뒤에 붙인다(순서 중요 — raw로 파싱한 뒤 걸러야 한다).
 *
 * 기본 스키마에서 딱 두 군데를 넓힌다:
 * - protocols.src에 blob — 이미지를 Blob→objectURL로 꽂기 때문에, 이게 없으면 기본값
 *   (http/https)에 걸려 문서의 이미지가 전부 지워진다.
 * - 표 부속 태그(caption/colgroup/col) — 기본 목록에 없어서 표 구조가 일부 무너진다.
 *   (colSpan/rowSpan/align 등 속성은 기본 스키마의 '*' 목록에 이미 들어 있다)
 */
const DOC_SANITIZE_SCHEMA = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), 'caption', 'colgroup', 'col'],
  protocols: {
    ...defaultSchema.protocols,
    src: [...(defaultSchema.protocols?.src ?? []), 'blob'],
  },
};

/**
 * blob: URL을 통과시키는 URL 변환기.
 *
 * react-markdown은 sanitize와 **별개로** 자체 urlTransform을 한 번 더 돌리는데, 기본 허용
 * 프로토콜이 http/https/irc/mailto/xmpp 뿐이라 blob:을 빈 문자열로 지운다. 이 뷰어는 이미지를
 * 인증 fetch → Blob → objectURL(blob:)로 꽂기 때문에, 이걸 안 풀면 이미지가 **한 장도** 안 나온다
 * (src="" 가 된다). blob:만 예외로 통과시키고 나머지는 기본 동작 그대로 둔다 — objectURL은
 * 우리가 이 세션에서 직접 만든 것이라 문서 내용에서 유래할 수 없다.
 */
function docUrlTransform(url: string): string {
  return url.startsWith('blob:') ? url : defaultUrlTransform(url);
}

/** 원본 파일을 새 탭으로 연다. 외부 CDN이라 인증 없이 접근 가능. */
function openSource(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * docparser 문서 뷰어.
 * 변환된 md를 이미지와 함께 인라인 렌더 — 비정형 문서(실적 발표 PDF 등) 열람에 활용.
 * 문서 파일을 못 받으면 원본 PDF 링크로 폴백한다.
 */
export function DocViewer({
  module,
  title,
  publisher,
  docDate,
  sourceUrl,
  mdPath,
  imagePaths,
  open,
}: DocViewerProps) {
  const { markdown, loading, unavailable, error } = useDocViewer(
    module,
    mdPath,
    imagePaths,
    open
  );

  return (
    <div>
      <div css={metaStyle}>
        <h2 css={titleStyle}>{title || '제목 없음'}</h2>
        <div css={submetaStyle}>
          {publisher && <span>{publisher}</span>}
          {docDate && <span>{docDate}</span>}
        </div>
        {sourceUrl && (
          <div css={actionsStyle}>
            <Button variant="outline" tone="neutral" onClick={() => openSource(sourceUrl)}>
              원본 파일 열기 ↗
            </Button>
          </div>
        )}
      </div>

      {/* md 경로 자체가 없는 문서 — 변환 산출물이 없다. */}
      {!mdPath && (
        <div css={fallbackStyle}>
          변환된 문서(md)가 없는 행입니다.
          {sourceUrl ? ' 위 “원본 파일 열기”로 확인해 주세요.' : ''}
        </div>
      )}

      {mdPath && loading && (
        <div css={msgStyle}>
          <Spinner />
          <span>문서 불러오는 중…</span>
        </div>
      )}

      {/* 문서 서빙 엔드포인트 미배포/404 등 — 본문 대신 원본 PDF로 유도 */}
      {mdPath && !loading && unavailable && (
        <div css={fallbackStyle}>
          문서 파일을 불러오지 못했습니다{error ? ` (${error})` : ''}.
          {sourceUrl
            ? ' 위 “원본 파일 열기”로 원본 문서를 확인할 수 있습니다.'
            : ''}
        </div>
      )}

      {mdPath && !loading && !unavailable && markdown && (
        <div css={mdStyle}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw, [rehypeSanitize, DOC_SANITIZE_SCHEMA]]}
            urlTransform={docUrlTransform}
          >
            {markdown}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
}
